<template>
  <div class="container mt-4">
    <!-- LOADING -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border text-primary"></div>
    </div>

    <!-- CONTENT -->
    <div v-else>
      <!-- CONFIGS LIST -->
      <div class="card mb-4 shadow-sm">
        <div class="card-header d-flex justify-content-between align-items-center">
          <div><i class="fas fa-mail-bulk"></i> Vos mail</div>
          <!-- BTN: SHOW FORM -->
          <button class="btn btn-sm btn-primary btn-icon-only d-flex align-items-center gap-2"
                  @click="showCreateForm = !showCreateForm">
            <i class="fas" :class="showCreateForm ? 'fa-minus' : 'fa-plus'"></i>
          </button>
        </div>

        <div class="card-body">
          <!-- EMPTY -->
          <div v-if="filteredMailConfigs.length === 0" class="text-muted">
            Aucune configuration existante.
          </div>

          <!-- LIST -->
          <ul v-else class="list-group">
            <li
                v-for="cfg in filteredMailConfigs"
                :key="cfg.uuid"
                class="list-group-item d-flex justify-content-between align-items-center"
            >
              <div style="cursor:pointer" @click="goTo(`/webmail/${cfg.uuid}`)">
                <strong>{{ cfg.email }}</strong>
                <div class="text-muted small">
                  Serveur : {{ cfg.server.name }}
                </div>
              </div>

              <div class="d-flex gap-2">
                <!-- EDIT -->
                <button
                    class="btn btn-sm btn-outline-primary"
                    @click.stop="editConfig(cfg)"
                >
                  <i class="fas fa-edit"></i>
                </button>

                <!-- DELETE -->
                <button
                    class="btn btn-sm btn-outline-danger"
                    @click.stop="deleteConfig(cfg)"
                >
                  <i class="fas fa-trash"></i>
                </button>

                <!-- DELETE -->
                <button
                    class="btn btn-sm btn-outline-primary"
                    @click.stop="goTo(`/webmail/${cfg.uuid}`)"
                >
                  <i class="fas fa-eye"></i>
                </button>
              </div>
            </li>

          </ul>
        </div>
      </div>

      <!-- CREATE FORM -->
      <div v-if="showCreateForm" class="card shadow-sm">
        <div class="card-header d-flex align-items-center gap-2">
          <i class="fas" :class="editingId ? 'fa-edit' : 'fa-plus-circle'"></i>
          {{ editingId ? 'Modifier la configuration' : 'Nouvelle configuration' }}
        </div>

        <div class="card-body">
          <form @submit.prevent="createConfig">

            <!-- SERVER SELECT -->
            <div class="mb-3">
              <label class="form-label">Serveur</label>
              <select class="form-select" v-model="form.serverId" required>
                <option value="" disabled>Choisir un serveur</option>
                <option v-for="server in serverConfigs" :key="server.uuid" :value="server.uuid">
                  {{ server.name }} ({{ server.smtp_server }})
                </option>
              </select>
            </div>
            <!-- NORMAL FORM IF NOT OUTLOOK -->
            <div>
              <div class="mb-3" v-if="!selectedServer.is_outlook">
                <label class="form-label">Adresse email</label>
                <input type="email" class="form-control" v-model="form.email" required>
              </div>

              <div class="mb-3" v-if="!selectedServer.is_outlook">
                <label class="form-label">Mot de passe</label>
                <input type="password" class="form-control" v-model="form.password" required>
              </div>

              <!-- Establishment -->
              <div class="mb-3">
                <label class="form-label">Établissement (optionnel)</label>
                <select class="form-select" v-model="form.establishmentId">
                  <option value="">Aucun</option>
                  <option v-for="e in establishment_set" :key="e.uid" :value="e.uid">
                    {{ e.name }}
                  </option>
                </select>
              </div>

              <!-- Gathering -->
              <div class="mb-3">
                <label class="form-label">Groupe (optionnel)</label>
                <select class="form-select" v-model="form.gatheringId">
                  <option value="">Aucun</option>
                  <option v-for="g in gathering_set" :key="g.uid" :value="g.uid">
                    {{ g.name }}
                  </option>
                </select>
              </div>
              <div class="d-flex gap-2">
                <div class="" v-if="!selectedServer.is_outlook">
                  <button type="submit" class="btn btn-success">
                    <i class="fas fa-check"></i>
                    {{ editingId ? 'Mettre à jour' : 'Valider' }}
                  </button>
                </div>
                <div v-if="selectedServer.is_outlook">
                  <button type="button"
                          class="mx-auto btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                          @click="connectMicrosoft">
                    <i class="fab fa-microsoft"></i>
                    Se connecter avec Microsoft
                  </button>
                </div>
                <div class="ms-auto">
                  <!-- Cancel -->
                  <button
                      type="button"
                      class="btn btn-secondary"
                      @click="showCreateForm = false; editingId = null"
                  >
                    <i class="fas fa-times"></i>
                    Annuler
                  </button>

                </div>
              </div>
            </div>

            <!-- Messages -->
            <div v-if="formError" class="alert alert-danger mt-3">{{ formError }}</div>
            <div v-if="formSuccess" class="alert alert-success mt-3">Création réussie !</div>

          </form>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import {generatePKCE} from "@/utils/pkce";
import CREATE_USER_MAIL_CONFIG from "@/gql/mutations/createUserMailConfig.graphql";
import UPDATE_USER_MAIL_CONFIG from "@/gql/mutations/updateUserMailConfig.graphql";
import DELETE_USER_MAIL_CONFIG from "@/gql/mutations/deleteUserMailConfig.graphql";
import ESTABLISHMENT_SET from "@/gql/queries/establishmentSet.graphql";
import GATHERING_SET from "@/gql/queries/gatheringSet.graphql";
import SERVER_CONFIG_LIST from "@/gql/queries/serverConfigList.graphql";
import USER_MAIL_CONFIG_LIST from "@/gql/queries/userMailConfigList.graphql";


import {gqlMutation, gqlQuery} from "@/gqlClient";

export default {
  name: "Webmail",

  data() {
    return {
      loading: true,
      serverConfigs: [],
      mailConfigs: [],
      establishment_set: [],
      gathering_set: [],
      showCreateForm: false,

      form: {
        serverId: "",
        email: "",
        password: "",
        establishmentId: "",
        gatheringId: "",
      },

      formError: "",
      formSuccess: false,
      editingId: null,
    };
  },

  computed: {
    selectedServer() {
      return this.serverConfigs.find(s => s.uuid === this.form.serverId) || {};
    },

    /** NE PAS AFFICHER LES CONFIG MICROSOFT */
    filteredMailConfigs() {
      return this.mailConfigs.filter(cfg => !cfg.server.is_outlook);
    }
  },

  methods: {
    editConfig(cfg) {
      this.showCreateForm = true;
      this.formSuccess = false;
      this.formError = "";
      this.editingId = cfg.uuid;

      this.form = {
        serverId: cfg.server.uuid,
        email: cfg.email,
        password: "",
        establishmentId: cfg.establishment.uid || "",
        gatheringId: cfg.gathering.uid || "",
      };
    },
    goTo(path) {
      this.$router.push(path);
    },

    async connectMicrosoft() {
      const clientId = "cf7412b8-9395-4441-97ad-d1858666a286";
      const tenantId = "common";

      // 1) Créer la config vide
      const resp = await gqlMutation(CREATE_USER_MAIL_CONFIG, {
        server_id: this.form.serverId,
        email: "",
        password: "",
        establishment: this.form.establishmentId || null,
        gathering: this.form.gatheringId || null,
      })

      const configId = resp.body.data.create_user_mail_config.user_mail_config.uuid;

      // PKCE
      const {code_verifier, code_challenge} = await generatePKCE();
      sessionStorage.setItem("outlook_pkce_verifier", code_verifier);
      sessionStorage.setItem("outlook_server_id", this.form.serverId);
      sessionStorage.setItem("outlook_step", "email"); // <---- important

      const redirect = encodeURIComponent(window.location.origin + "/oauth/callback/outlook");

      const scopes = encodeURIComponent([
        "openid",
        "profile",
        "email",
        "offline_access",
        "User.Read",
      ].join(" "));

      window.location.href =
          `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`
          + `?client_id=${clientId}`
          + `&response_type=code`
          + `&redirect_uri=${redirect}`
          + `&response_mode=query`
          + `&code_challenge=${code_challenge}`
          + `&code_challenge_method=S256`
          + `&scope=${scopes}`
          + `&state=${configId}`;
    },
    createConfig() {
      this.formError = "";

      if (!this.form.serverId) {
        this.formError = "Veuillez choisir un serveur.";
        return;
      }

      if (!this.selectedServer.is_outlook && !this.editingId) {
        if (!this.form.email || !this.form.password) {
          this.formError = "Email et mot de passe requis.";
          return;
        }
      }

      const payload = {
        server_id: this.form.serverId,
        email: this.form.email,
        password: this.form.password || undefined,
        establishment: this.form.establishmentId || null,
        gathering: this.form.gatheringId || null,
      };

      const mutation = this.editingId
          ? UPDATE_USER_MAIL_CONFIG
          : CREATE_USER_MAIL_CONFIG;

      const args = this.editingId
          ? { uuid: this.editingId, ...payload }
          : payload;

      gqlMutation(mutation, args).then(resp => {
        if (resp.errors) {
          this.formError = resp.errors[0].message;
          return;
        }

        this.formSuccess = true;
        this.showCreateForm = false;
        this.editingId = null;
        console.log(this.$store.state.profile.data.uid)
        gqlQuery(USER_MAIL_CONFIG_LIST, {
          consultant_uid: this.$store.state.profile.data.uid,
        }).then(r => {
          this.mailConfigs = r.body.data.user_mail_config_list || [];
        });
      });
    },
    deleteConfig(cfg) {
      if (!confirm(`Supprimer la configuration ${cfg.email} ?`)) return;
      gqlMutation(DELETE_USER_MAIL_CONFIG, {id: cfg.uuid})
          .then(resp => {
            if (resp.errors) {
              alert(resp.errors[0].message);
              return;
            }

            this.mailConfigs = this.mailConfigs.filter(
                c => c.uuid !== cfg.uuid
            );
          });
    },
  },

  mounted() {
    console.log("Mounted: " + this.$store.state.profile.data.uid)
    Promise.all([
      gqlQuery(ESTABLISHMENT_SET, {consultant_uid: this.$store.state.profile.data.uid}),
      gqlQuery(GATHERING_SET, {consultant_uid: this.$store.state.profile.data.uid}),
      gqlQuery(SERVER_CONFIG_LIST, {}),
      gqlQuery(USER_MAIL_CONFIG_LIST, {consultant_uid: this.$store.state.profile.data.uid}),
    ]).then(([estabs, gathers, servers, configs]) => {
      this.serverConfigs = servers.body.data.server_config_list || [];
      this.mailConfigs = configs.body.data.user_mail_config_list || [];
      this.establishment_set = estabs.body.data.establishment_set || [];
      this.gathering_set = gathers.body.data.gathering_set || [];
      this.loading = false;
      // get query params if no_redirect is set and is true then do not redirect
      const noRedirect = this.$route.query.no_redirect === "true";
      if (noRedirect) return;
      if (this.mailConfigs.length == 1) {
        // redirect to firt mailConfigs
        this.goTo(`/webmail/${this.mailConfigs[0].uuid}`);
      }
    });
  },
};
</script>
